// ProtectedRoute.jsx
import { Navigate, useLocation } from "react-router-dom";
import React from "react";

/**
 * ترجع مصفوفة نصوص تمثل أسماء الرُتب (role names)
 * تتعامل مع:
 * - null/undefined
 * - string (json-string, "admin,user", أو "admin")
 * - Array (of strings, of objects)
 * - Object مفرد (مثال: { id: 1, name: 'student' } أو { student: true })
 */
function normalizeRoles(raw) {
  if (!raw && raw !== 0) return [];

  // لو كان سلسلة نصية: حاول نعمل JSON.parse أولاً لو شكلها JSON،
  // ثم نتعامل مع الفواصل، وإلا نرجع السلسلة نفسها كمصفوفة.
  if (typeof raw === "string") {
    const s = raw.trim();
    // محاولة تفريغ JSON (لو كانت "[...]" أو "{\"name\":\"...\"}" أو "\"admin\"")
    if (s.startsWith("[") || s.startsWith("{") || s.startsWith('"')) {
      try {
        const parsed = JSON.parse(s);
        return normalizeRoles(parsed);
      } catch (e) {
        // لا نفشل؛ نستمر لمعالجة كـ comma-separated أو single
      }
    }
    if (s.includes(",")) {
      return s
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean);
    }
    return [s];
  }

  // لو كانت مصفوفة
  if (Array.isArray(raw)) {
    const out = raw.flatMap((item) => {
      if (item === null || item === undefined) return [];

      // عنصر نصي
      if (typeof item === "string") return item.trim() ? [item.trim()] : [];

      // عنصر كائن
      if (typeof item === "object") {
        // مفاتيح شائعة للاسم
        const name =
          item.name ||
          item.title ||
          item.role ||
          item.slug ||
          item.guard_name ||
          item.display_name;

        if (typeof name === "string" && name.trim()) return [name.trim()];

        // في بعض الأحيان item.role هو كائن { name: '...' }
        if (item.role && typeof item.role === "object") {
          const nested = item.role.name || item.role.title || item.role.slug;
          if (typeof nested === "string" && nested.trim()) return [nested.trim()];
        }

        // fallback: اجلب أول قيمة نصية موجودة في الكائن
        for (const k in item) {
          if (typeof item[k] === "string" && item[k].trim()) {
            return [item[k].trim()];
          }
        }
      }

      return [];
    });

    // إزالة التكرارات والفارغات
    return Array.from(new Set(out)).filter(Boolean);
  }

  // لو كان كائن مفرد (object)
  if (typeof raw === "object") {
    // لو فيه حقل name
    if (typeof raw.name === "string" && raw.name.trim()) {
      return [raw.name.trim()];
    }

    // لو فيه حقول role / roles بداخل
    if (raw.roles || raw.role) {
      return normalizeRoles(raw.roles || raw.role);
    }

    // لو كان خريطة boolean مثل { student: true, admin: false }
    const truthyKeys = Object.keys(raw).filter(
      (k) => raw[k] === true || raw[k] === 1 || raw[k] === "1"
    );
    if (truthyKeys.length) return truthyKeys;

    // fallback: اجمع القيم النصية داخل الكائن
    const vals = Object.values(raw)
      .filter((v) => typeof v === "string" && v.trim())
      .map((v) => v.trim());
    return Array.from(new Set(vals)).filter(Boolean);
  }

  // أي نوع آخر -> لا شيء
  return [];
}

function ProtectedRoute({ children, allowedRoles = [] }) {
  const location = useLocation();

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (e) {
      return null;
    }
  })();

  const token = localStorage.getItem("accessToken");

  if (!token || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // إذا لم تحدد أدوار => اسمح للمستخدمين المسجلين
  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
    return children;
  }

  // احصل على بيانات الرتب من أماكن محتملة داخل user
  // (تغيير طفيف: نفضل user.user.roles ثم user.user.role ثم user.roles ثم user.role)
  const candidate =
    user?.user?.roles ||
    user?.user?.role ||
    user?.roles ||
    user?.role ||
    user;

  const roles = normalizeRoles(candidate || []);
  // مقارنة غير حساسة لحالة الأحرف (lowercase لكل الأطراف)
  const allowedNormalized = allowedRoles.map((r) => String(r).trim().toLowerCase());
  const userRolesNormalized = roles.map((r) => String(r).trim().toLowerCase());

  const hasAllowedRole = userRolesNormalized.some((r) => allowedNormalized.includes(r));
  if (!hasAllowedRole) {
    // if (userRolesNormalized.includes("general_admin")) {
    return <Navigate to="/" replace />;
    // }
    // return <Navigate to="/login" replace />;
  }

  return children;
}

export default ProtectedRoute;
